
// The DANVA domain-data SHAPE (task section 6/7) and the pure helpers that
// create/read/mutate it - kept separate from FeatureManager (which only
// knows "some namespace, some data") and separate from any UI. Nothing here
// touches THREE.js/DOM.
//
// Namespace key used on `feature.domainData` (see Feature.js) - one constant,
// never a magic string repeated across files.
export const DANVA_NAMESPACE = "danva";

// Task section 7: "Undgå en simpel boolean som isDanva = true... Modelversion
// skal være eksplicit data." - `model`/`version`/`objectType` are always
// present and explicit; there is no separate boolean anywhere - a feature
// IS a DANVA object exactly when `feature.domainData.danva` exists, and
// isDanvaObject() below is a query, not a stored flag.
//
// `lifecycle`/`history` follow DANVA's own official database-history/
// lifecycle definitions (task section 35) - deliberately left as an opaque,
// model-defined bag here (`lifecycle: null`, `history: []`) rather than
// invented statuses/events, since no official definition is available in
// this project yet (see the task's own final report). The shape is real;
// the vocabulary inside `lifecycle` is not fabricated.
// Real DANVAND identity, not PointView's own `feature.id` (which is not
// GUID-shaped - see kerne/KER_GuidType.xsd's own pattern,
// `[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}`,
// which `crypto.randomUUID()` satisfies exactly). Generated ONCE, here, at
// classification time - never regenerated on re-export (see
// DanvaExchangeAdapter.js's own header comment on why GUID stability
// matters: the DANVAND vertical-slice task's own explicit requirement is
// that re-exporting the same registered object must reuse its existing
// identity, not mint a new one each time).
export function generateDanvaGuid(){
	return crypto.randomUUID();
}

export function createDanvaClassification({model, version, objectType, guid, attributes = {}, relations = [], provenance = {}}){
	return {
		model,
		version,
		objectType,
		guid: guid || generateDanvaGuid(),
		attributes,
		relations,
		lifecycle: null,
		history: [],
		// Task section 20 (proveniens): per-attribute-name origin tag, one of
		// "measured" | "calculated" | "user-entered" | "default" | "imported".
		// A plain object keyed by attribute name (mirrors `attributes` itself)
		// rather than a new framework - see DanvaMapping.js's own doc comment
		// for how a mapped/measured value gets its provenance set.
		provenance,
	};
}

export function isDanvaObject(feature){
	return !!(feature && feature.domainData && feature.domainData[DANVA_NAMESPACE]);
}

export function getDanvaData(feature){
	return isDanvaObject(feature) ? feature.domainData[DANVA_NAMESPACE] : null;
}

// Registers `feature` as a DANVA object of the given model/version/type via
// the ONE existing domain-data mutation path (FeatureManager.setDomainData) -
// never a parallel feature-mutation mechanism. Geometry/properties are
// completely untouched (task section 16/40: "Geometri skal bevares... Undgå
// at kopiere geometrien unødvendigt til en separat parallel feature").
export function classifyAsDanva(featureManager, featureId, options){
	const classification = createDanvaClassification(options);
	featureManager.setDomainData(featureId, DANVA_NAMESPACE, classification);
	return classification;
}

// Un-registers a feature as DANVA - the feature itself is untouched and
// remains exactly the PointView feature it always was (task section 16).
export function unclassifyDanva(featureManager, featureId){
	featureManager.clearDomainData(featureId, DANVA_NAMESPACE);
}

// Patches attribute values (e.g. from a form edit, or an accepted mapping
// suggestion - see DanvaMapping.js) - merges into the existing attribute bag
// rather than replacing it, and records provenance per changed attribute so
// a later re-edit doesn't silently keep a stale "measured"/"default" tag
// (task section 20).
export function setDanvaAttributes(featureManager, feature, attributePatch, provenanceTag){
	const current = getDanvaData(feature);
	if(!current) return;

	const nextAttributes = {...current.attributes, ...attributePatch};
	const nextProvenance = {...current.provenance};
	if(provenanceTag){
		for(const key of Object.keys(attributePatch)) nextProvenance[key] = provenanceTag;
	}

	featureManager.setDomainData(feature.id, DANVA_NAMESPACE, {
		...current,
		attributes: nextAttributes,
		provenance: nextProvenance,
	});
}
